import React, { useState } from "react";
import { Loader, RefreshCw, Search, ChevronRight, ChevronDown } from "lucide-react";

export const SchemaTab = ({
    allStreams,
    streams, // Fallback if allStreams is null
    schemaLoading,
    fetchAllStreams,
    saveSchemaStreamConfig,
    saving,
    onUpdateStream
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [streamFilter, setStreamFilter] = useState("all");
    const [expandedStreams, setExpandedStreams] = useState(new Set());

    // Show loading state while fetching streams
    if (schemaLoading) {
        return (
            <div className="p-6">
                <div className="bg-white rounded-lg shadow p-12 flex flex-col items-center justify-center">
                    <Loader className="w-8 h-8 text-primary-600 animate-spin mb-4" />
                    <p className="text-gray-500">Loading streams...</p>
                </div>
            </div>
        );
    }

    // Use allStreams if available, otherwise fall back to configured streams
    const currentStreams = allStreams || streams || [];

    const enabledCount = currentStreams.filter((s) => s.enabled).length;

    const filteredStreams = currentStreams.filter((stream) => {
        const streamName = stream.name || "";
        const matchesSearch = streamName
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        const matchesFilter =
            streamFilter === "all" ||
            (streamFilter === "enabled" && stream.enabled) ||
            (streamFilter === "disabled" && !stream.enabled);
        return matchesSearch && matchesFilter;
    });

    const toggleStreamExpanded = (streamName) => {
        const newExpanded = new Set(expandedStreams);
        if (newExpanded.has(streamName)) {
            newExpanded.delete(streamName);
        } else {
            newExpanded.add(streamName);
        }
        setExpandedStreams(newExpanded);
    };

    const handleToggleStream = (streamName) => {
        const updatedStreams = currentStreams.map(s =>
            s.name === streamName ? { ...s, enabled: !s.enabled } : s
        );
        onUpdateStream(updatedStreams);
    };

    const handleToggleAll = () => {
        const allEnabled = enabledCount === currentStreams.length;
        const updatedStreams = currentStreams.map(s => ({ ...s, enabled: !allEnabled }));
        onUpdateStream(updatedStreams);
    };

    const handleUpdateSyncMode = (streamName, syncMode) => {
        const updatedStreams = currentStreams.map(s =>
            s.name === streamName ? { ...s, syncMode } : s
        );
        onUpdateStream(updatedStreams);
    };

    return (
        <div className="p-6">
            <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">Schema</h3>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={fetchAllStreams}
                                className="p-2 text-gray-400 hover:text-gray-600"
                                title="Refresh streams"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="relative flex-1 max-w-xs">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search stream or field name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm"
                            />
                        </div>
                        <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                            <option>Streams and fields</option>
                        </select>
                    </div>

                    <div className="flex items-center space-x-6 mt-4 text-sm">
                        <button
                            onClick={() => setStreamFilter("all")}
                            className={`pb-2 ${streamFilter === "all"
                                ? "text-primary-600 border-b-2 border-primary-600 font-medium"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setStreamFilter("enabled")}
                            className={`pb-2 ${streamFilter === "enabled"
                                ? "text-primary-600 border-b-2 border-primary-600 font-medium"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            Enabled streams
                        </button>
                        <button
                            onClick={() => setStreamFilter("disabled")}
                            className={`pb-2 ${streamFilter === "disabled"
                                ? "text-primary-600 border-b-2 border-primary-600 font-medium"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            Disabled streams
                        </button>
                    </div>
                </div>

                <div className="divide-y divide-gray-200">
                    <div className="grid grid-cols-12 px-6 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                        <span className="col-span-1">
                            <input
                                type="checkbox"
                                checked={
                                    enabledCount === currentStreams.length &&
                                    currentStreams.length > 0
                                }
                                onChange={handleToggleAll}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                        </span>
                        <span className="col-span-6">{currentStreams.length} streams</span>
                        <span className="col-span-3">Sync mode</span>
                        <span className="col-span-2">Fields</span>
                    </div>

                    {filteredStreams.map((stream) => (
                        <div key={stream.name}>
                            <div className="grid grid-cols-12 px-6 py-4 text-sm items-center hover:bg-gray-50">
                                <span className="col-span-1">
                                    <input
                                        type="checkbox"
                                        checked={stream.enabled}
                                        onChange={() => handleToggleStream(stream.name)}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                </span>
                                <span className="col-span-6 flex items-center">
                                    <button
                                        onClick={() => toggleStreamExpanded(stream.name)}
                                        className="mr-2 text-gray-400 hover:text-gray-600"
                                    >
                                        {expandedStreams.has(stream.name) ? (
                                            <ChevronDown className="w-4 h-4" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4" />
                                        )}
                                    </button>
                                    <span className="text-gray-900">{stream.name}</span>
                                </span>
                                <span className="col-span-3">
                                    {stream.enabled && (
                                        <select
                                            value={stream.syncMode || "full_refresh_overwrite"}
                                            onChange={(e) =>
                                                handleUpdateSyncMode(
                                                    stream.name,
                                                    e.target.value,
                                                )
                                            }
                                            className="border border-gray-300 rounded px-2 py-1 text-xs"
                                        >
                                            {(
                                                stream.supportedSyncModes || [
                                                    "full_refresh_overwrite",
                                                ]
                                            ).map((mode) => (
                                                <option key={mode} value={mode}>
                                                    {mode.replace(/_/g, " ")}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </span>
                                <span className="col-span-2 text-gray-500">
                                    {stream.enabled && stream.jsonSchema?.properties ?
                                        `${Object.keys(stream.jsonSchema.properties).length} fields` : "-"}
                                </span>
                            </div>

                            {/* Expanded Stream Details (Fields) */}
                            {expandedStreams.has(stream.name) && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-b border-gray-100 pl-16">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Fields</div>
                                    {stream.jsonSchema?.properties ? (
                                        <div className="grid grid-cols-3 gap-2">
                                            {Object.keys(stream.jsonSchema.properties).map((fieldName) => (
                                                <div key={fieldName} className="text-sm font-medium text-gray-700 bg-white px-3 py-1.5 rounded border border-gray-200">
                                                    {fieldName}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">No field information available</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {currentStreams.length === 0 && !schemaLoading && (
                        <div className="px-6 py-12 text-center text-gray-500">
                            No streams available
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-200">
                    <button
                        onClick={saveSchemaStreamConfig}
                        disabled={saving}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400"
                    >
                        {saving ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};
